import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Button from '@/components/ui/Button';
import Logo from '@/components/ui/Logo';
import {
  RiFileList3Line,
  RiUserSmileLine,
  RiLogoutBoxLine,
  RiBookMarkedFill,
} from 'react-icons/ri';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Cases', href: '/cases', icon: RiFileList3Line, disabled: false },
    {
      name: 'Knowledge Base',
      href: '/knowledge-base',
      icon: RiBookMarkedFill,
      disabled: false,
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white shadow-soft border-b border-neutral-200/80 sticky top-0 z-50 backdrop-blur-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex items-center space-x-8">
              <Link to="/cases" className="transition-transform hover:scale-105">
                <Logo size="md" />
              </Link>

              <div className="hidden md:flex space-x-2">
                {navigation.map(item => {
                  const Icon = item.icon;
                  const isActive = location.pathname.startsWith(item.href);

                  if (item.disabled) {
                    return (
                      <div
                        key={item.name}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-neutral-400 cursor-not-allowed select-none relative"
                      >
                        <Icon className="h-5 w-5" />
                        <span className="flex items-center gap-2">
                          <span>{item.name}</span>
                          <span className="text-[9px] font-semibold tracking-wide uppercase bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded border border-neutral-200">
                            soon
                          </span>
                        </span>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-50 text-primary-700 shadow-soft'
                          : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              {user && (
                <div className="flex items-center space-x-3">
                  <div className="hidden md:flex items-center gap-3 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200/80">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm">
                      {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-neutral-700">
                      {user.name || user.email}
                    </span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="flex items-center gap-2"
                  >
                    <RiLogoutBoxLine className="h-4 w-4" />
                    <span>Logout</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 w-full">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-neutral-200/80 mt-auto shadow-soft">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <Logo size="sm" />
              <span className="text-sm text-neutral-500">
                © 2025 ImmStack. All rights reserved.
              </span>
            </div>

            <div className="flex items-center space-x-6">
              <a
                href="https://immstack.ai/legal/privacy_notice.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
              >
                Privacy Notice
              </a>
              <a
                href="https://immstack.ai/legal/terms_of_service.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
